import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Card,
    Divider,
    Group,
    Kbd,
    Paper,
    Stack,
    Stepper,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
} from "@mantine/core";
import {
    IconArrowLeft,
    IconArrowRight,
    IconBrandTeams,
    IconCheck,
    IconMail,
    IconSearch,
    IconShieldLock,
    IconUser,
    IconWifi,
    TablerIcon,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import CollegeLogo from "@assets/gc_logo_navy.svg"
import { YourAccountStep } from "./YourAccountStep";
import { WiFiStep } from "./WiFiStep";
import { AuthenticatorStep } from "./AuthenticatorStep";
import { useAuthenticationMethods, useCompleteInductionStep, useInductionProgress, useStartInduction } from "../queries";
import { useStaffOverrideStore } from "@stores/useStaffOverrideStore";
import { useHostMode, useSetViewMode } from "@stores/usePortalModeStore";
import { PortalStep } from "./PortalStep";
import { EmailStep } from "./EmailStep";
import { TeamsStep } from "./TeamsStep";
import { ConsentStep } from "./ConsentStep";

export const inductionStepIds = [
    "details",
    "wifi",
    "authenticator",
    "portal",
    "outlook",
    "teams",
    "consent",
] as const;

export type InductionStepId = typeof inductionStepIds[number];

const steps: Array<{ id: InductionStepId; label: string, description: string, icon: TablerIcon }> = [
    { id: "details", label: "Your account", description: "Log in credentials", icon: IconUser },
    { id: "wifi", label: "WiFi", description: "Connect your device", icon: IconWifi },
    { id: "authenticator", label: "Authenticator", description: "Add a verification method", icon: IconShieldLock },
    { id: "portal", label: "Student Portal", description: "Learn the basics of the portal", icon: IconSearch },
    { id: "outlook", label: "Email", description: "Access Outlook", icon: IconMail },
    { id: "teams", label: "Teams", description: "Find your induction work", icon: IconBrandTeams },
    { id: "consent", label: "Photography/videography permissions", description: "", icon: IconCheck },
];

type InductionFrameProps = {
    step?: InductionStepId;
    onStepChange?: (step?: InductionStepId) => void;
    showLanding?: boolean;
};

export const InductionFrame: React.FC<InductionFrameProps> = ({ step, onStepChange, showLanding = false }) => {
    const [localActive, setLocalActive] = useState(0);
    const [completed, setCompleted] = useState(false);
    const [consentChoice, setConsentChoice] = useState<boolean | null>(null);
    const hostMode = useHostMode();
    const isStaffSession = hostMode === "staff";
    const { data: progress } = useInductionProgress(!isStaffSession);
    const startInduction = useStartInduction();
    const completeStep = useCompleteInductionStep();
    const setViewMode = useSetViewMode();
    const studentOverride = useStaffOverrideStore((state) => state.override);
    const overrideType = useStaffOverrideStore((state) => state.overrideType);
    const setOverride = useStaffOverrideStore((state) => state.setOverride);
    const isStaffImpersonatingStudent = hostMode === "staff" && overrideType === "student" && Boolean(studentOverride?.trim());

    const active = Math.max(0, steps.findIndex((candidate) => candidate.id === step));
    const activeStep = step ? active : localActive;

    const setStepIndex = (index: number) => {
        const nextIndex = Math.min(Math.max(index, 0), steps.length - 1);
        if (onStepChange) {
            onStepChange(steps[nextIndex].id);
            return;
        }

        setLocalActive(nextIndex);
    };
    const selectStepperStep = (index: number) => {
        const selectedStep = steps[index];
        if (!selectedStep || (!isStaffSession && !progress?.[selectedStep.id])) {
            return;
        }

        setStepIndex(index);
    };
    const nextStep = async () => {
        if (!isStaffSession) {
            const currentStep = steps[activeStep].id;
            await completeStep.mutateAsync({
                step: currentStep,
                ...(currentStep === "consent" ? { photoMarketingConsent: consentChoice ?? progress?.photoMarketingConsent ?? true } : {}),
            });
        }
        if (activeStep === steps.length - 1) {
            setCompleted(true);
            return;
        }
        setStepIndex(activeStep + 1);
    };
    const start = async () => {
        if (!isStaffSession) {
            await startInduction.mutateAsync();
        }
        const resumeIndex = steps.findIndex((candidate) => !progress?.[candidate.id]);
        setStepIndex(resumeIndex >= 0 ? resumeIndex : 0);
    };
    const prevStep = () => {
        if (activeStep === 0 && onStepChange) {
            onStepChange(undefined);
            return;
        }

        setStepIndex(activeStep - 1);
    };

    const { data: authenticationMethods } = useAuthenticationMethods()

    const has2fa = useMemo(() => authenticationMethods && authenticationMethods?.length > 1, [authenticationMethods])
    const showComplete = completed || Boolean(progress?.dateInductionCompleted && step === "consent");

    return (
        <Box bg="gray.0" h="100%" mih="0">
            <Stack gap={0} h="100%" mih="0">
                <Group wrap="nowrap" bg="white" p="md" pr="xl" gap="xl" style={{ borderBottom: "1px solid var(--mantine-color-gray-2)" }}>
                    <img src={CollegeLogo} style={{ maxHeight: 38 }} />
                    <Stepper 
                        active={showComplete ? steps.length : showLanding ? -1 : activeStep} 
                        onStepClick={selectStepperStep}
                        size="sm"
                        className="grow"
                    >
                        {steps.map((step) => (
                            <Stepper.Step
                                key={step.id}
                                label={step.label}
                                description={step.description}
                                allowStepClick={isStaffSession || Boolean(progress?.[step.id])}
                            />
                        ))}
                    </Stepper>
                    {isStaffImpersonatingStudent ? (
                        <Button
                            component={Link}
                            to="/home"
                            variant="light"
                            size="xs"
                            leftSection={<IconArrowLeft size={14} />}
                            onClick={() => {
                                setOverride(null);
                                setViewMode("staff");
                            }}
                            className="shrink-0"
                        >
                            Return to staff portal
                        </Button>
                    ) : null}
                </Group>

                {showComplete ?

                <InductionComplete />

                : showLanding ? 
                
                <Stack bg="gray.0" h="100%" mih="0" align="center" justify="center">
                    <Card w="fit-content" withBorder h={500} radius="lg">
                        <Stack h="100%" align="center" justify="center" gap="lg" px="xl">
                            <img src={CollegeLogo} alt="Gloucestershire College" style={{ width: 220, maxWidth: "70%" }} />
                            <Stack align="center" gap="sm">
                                <Title order={1} ta="center">Welcome to your IT induction</Title>
                                <Text fw="500" c="dimmed" size="lg" ta="center" maw={560} mt="xl">
                                    It should take 10 - 15 minutes and will get you up and running with our systems.
                                </Text>
                                <Text fw="500" c="dimmed" size="lg" ta="center" maw={560} mt="md" mb="xl">
                                    If you get stuck, raise your hand and someone will come to help.
                                </Text>
                            </Stack>
                            <Button size="xl" className="animate-pulse" rightSection={<IconArrowRight size={18} />} onClick={() => void start()} loading={startInduction.isPending}>
                                {progress?.dateInductionStarted ? "Continue induction" : "Start induction"}
                            </Button>
                        </Stack>
                    </Card>
                </Stack>

                :
                
                <Stack flex={1} gap="0">
                    <InductionPanel icon={steps[activeStep].icon} title={steps[activeStep].label}>
                        {activeStep === 0 && (
                                <YourAccountStep />
                        )}

                        {activeStep === 1 && (
                                <WiFiStep />
                        )}

                        {activeStep === 2 && (
                                <AuthenticatorStep />
                        )}

                        {activeStep === 3 && (
                                <PortalStep />
                        )}

                        {activeStep === 4 && (
                                <EmailStep />
                        )}

                        {activeStep === 5 && (
                                <TeamsStep />
                        )}

                        {activeStep === 6 && (
                                <ConsentStep
                                    checked={consentChoice ?? progress?.photoMarketingConsent ?? true}
                                    onChange={setConsentChoice}
                                />
                        )}
                    </InductionPanel>
                    {/* <Divider /> */}
                    <Group justify="center" py="xl" w="100%">
                        <Group pb="xl">
                            <Tooltip label="Previous step">
                                <ActionIcon size="input-lg" variant="default" onClick={prevStep} aria-label={activeStep === 0 ? "Back to welcome" : "Previous step"}>
                                    <IconArrowLeft size={20} />
                                </ActionIcon>
                            </Tooltip>
                            <Button size="lg" rightSection={completed ? <IconCheck size={18} /> : <IconArrowRight size={18} />} onClick={() => void nextStep()} loading={completeStep.isPending} disabled={completed || (activeStep === 2 && !has2fa)}>
                                {completed ? "Completed" : activeStep === steps.length - 1 ? "Complete induction" : "Next"}
                            </Button>
                        </Group>
                    </Group>
                </Stack>}
            </Stack>
        </Box>
    );
};

function InductionComplete() {
    return (
        <Stack bg="gray.0" h="100%" mih="0" align="center" justify="center" p="md">
            <Card w="fit-content" maw={620} withBorder radius="lg" p="xl">
                <Stack align="center" gap="lg" px="xl" py="md">
                    <ThemeIcon size={72} radius="xl" color="green" variant="light">
                        <IconCheck size={40} stroke={2} />
                    </ThemeIcon>
                    <Stack align="center" gap="xs">
                        <Title order={1} ta="center">You're all set</Title>
                        <Text c="dimmed" size="lg" ta="center" maw={480}>
                            You can now sign out by pressing <Kbd>Ctrl</Kbd> + <Kbd>Alt</Kbd> + <Kbd>Del</Kbd> and selecting <Text fw={700} span>Sign out</Text>
                        </Text>
                    </Stack>
                </Stack>
            </Card>
        </Stack>
    );
}

function InductionPanel({
    icon: Icon,
    title,
    children,
}: {
    icon: TablerIcon;
    title: string;
    children: React.ReactNode;
}) {
    return (
        <Stack gap="lg" flex={1} align="center" mt={'md'}>
            <Group gap="xs" wrap="nowrap" align="center">
                <ThemeIcon radius="sm" size={42} color="gray" variant="transparent">
                    <Icon />
                </ThemeIcon>
                <Title order={2} size="h3">{title}</Title>
            </Group>
            {children}
        </Stack>
    );
}
